/**
 * Devices Command
 * Manage device sessions
 */

import { Command } from 'commander';
import { listDevices, revokeDevice, revokeAllOtherDevices } from '../api/devices.js';
import { isAuthenticated } from '../utils/config.js';
import * as output from '../utils/output.js';

function requireAuth(): void {
    if (!isAuthenticated()) {
        output.error('Not logged in. Run `paean login` first.');
        process.exit(1);
    }
}

export const devicesCommand = new Command('devices')
    .description('List and manage active device sessions')
    .option('--json', 'Output as JSON')
    .action(async (options) => {
        requireAuth();

        try {
            const devices = await listDevices();

            if (options.json) {
                output.json(devices);
                return;
            }

            if (devices.length === 0) {
                output.dim('No active device sessions.');
                return;
            }

            output.header('Active Device Sessions');

            for (const device of devices) {
                const current = device.isCurrentDevice ? output.colors.success(' (current)') : '';
                const name = device.deviceName || device.deviceType || 'Unknown';
                const type = output.colors.dim(`[${device.deviceType}]`);
                const lastActive = device.lastActiveAt
                    ? output.colors.dim(new Date(device.lastActiveAt).toLocaleString())
                    : output.colors.dim('unknown');

                output.listItem(`${output.colors.info(String(device.id))}  ${name} ${type}${current}  ${lastActive}`);
            }

            output.newline();
            output.dim('Revoke: paean devices revoke <id> | Revoke all: paean devices revoke-all');
            output.newline();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to list devices: ${errorMessage}`);
            process.exit(1);
        }
    });

// Subcommand: devices revoke
devicesCommand
    .command('revoke <id>')
    .description('Revoke a device session')
    .action(async (id: string) => {
        requireAuth();

        try {
            const success = await revokeDevice(id);
            if (success) {
                output.success('Device session revoked.');
            } else {
                output.error('Failed to revoke device session.');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to revoke device: ${errorMessage}`);
            process.exit(1);
        }
    });

// Subcommand: devices revoke-all
devicesCommand
    .command('revoke-all')
    .description('Revoke all other device sessions')
    .action(async () => {
        requireAuth();

        try {
            const success = await revokeAllOtherDevices();
            if (success) {
                output.success('All other device sessions revoked.');
            } else {
                output.error('Failed to revoke device sessions.');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to revoke devices: ${errorMessage}`);
            process.exit(1);
        }
    });
